/**
 * 서명 캔버스 공통 로직 (동의서 / 협찬 / 시술 확인 공용).
 *
 * 인라인 Alpine 컴포넌트에 스프레드해서 사용한다:
 *   window.someForm = () => ({ ...window.qrshopSignaturePad(), ... })
 *
 * 해결하는 문제:
 * - 서명 중 캔버스 밖 텍스트가 드래그 선택돼 획이 끊기는 현상
 *   (pointerdown 이 무시되는 경로에서도 기본 동작을 먼저 막고,
 *    그리는 동안 document 레벨에서 selectstart/dragstart 를 차단한다)
 * - 멀티터치/손바닥 접촉으로 획이 초기화되는 현상 (주 포인터만 사용)
 * - 캔버스가 아직 0폭이거나 레이아웃/회전으로 크기가 바뀌어 좌표가 어긋나는 현상
 */

const LINE_WIDTH = 2.5;
const STROKE_STYLE = '#111';

function applyStrokeStyle(ctx) {
  ctx.lineWidth = LINE_WIDTH;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = STROKE_STYLE;
}

export function createSignaturePad() {
  return {
    ctx: null,
    drawing: false,
    hasSignature: false,
    // 서명 캔버스 요소 직접 참조(중첩 x-for 안이라 $refs 로는 안 잡힌다).
    sigCanvas: null,
    sigPointerId: null,
    sigObserver: null,
    sigBlockers: null,

    setupCanvas(el) {
      const canvas = el || this.sigCanvas;
      if (!canvas) return;
      this.sigCanvas = canvas;

      // 레이아웃 전(0폭)에 초기화되면 그리기가 안 되므로 다음 프레임에 재시도.
      if (!this.syncCanvasSize(canvas)) {
        window.requestAnimationFrame(() => this.setupCanvas(canvas));
        return;
      }

      // 회전/키보드 노출 등으로 캔버스 크기가 바뀌면 좌표가 어긋나므로 재동기화.
      // 그리는 중에는 진행 중인 path 가 끊기므로 건너뛴다.
      if (!this.sigObserver && typeof ResizeObserver !== 'undefined') {
        this.sigObserver = new ResizeObserver(() => {
          if (!this.drawing) this.syncCanvasSize(canvas);
        });
        this.sigObserver.observe(canvas);
      }
    },

    /** 표시 크기에 맞춰 캔버스 백버퍼를 맞춘다. 기존 서명은 보존. */
    syncCanvasSize(canvas) {
      const ratio = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return false;

      const width = Math.round(rect.width * ratio);
      const height = Math.round(rect.height * ratio);

      if (canvas.width === width && canvas.height === height && this.ctx) {
        return true;
      }

      const previous =
        this.hasSignature && canvas.width && canvas.height
          ? canvas.toDataURL('image/png')
          : '';

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.scale(ratio, ratio);
      applyStrokeStyle(ctx);
      this.ctx = ctx;

      if (previous) {
        const image = new Image();
        image.onload = () => ctx.drawImage(image, 0, 0, rect.width, rect.height);
        image.src = previous;
      }

      return true;
    },

    pointerPos(e) {
      const rect = this.sigCanvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    },

    /**
     * 그리는 동안 페이지 전체의 텍스트 선택/드래그를 막는다.
     * 캔버스 밖에서 pointerup 이 끝나는 경우(포인터 캡처 실패)를 대비해
     * window 종료 이벤트도 함께 건다.
     */
    blockSelection() {
      if (this.sigBlockers) return;

      const prevent = (e) => e.preventDefault();
      const finish = (e) => this.endDraw(e);
      const body = document.body;
      const restore = {
        userSelect: body.style.userSelect,
        webkitUserSelect: body.style.webkitUserSelect,
      };

      document.addEventListener('selectstart', prevent, true);
      document.addEventListener('dragstart', prevent, true);
      window.addEventListener('pointerup', finish, true);
      window.addEventListener('pointercancel', finish, true);
      body.style.userSelect = 'none';
      body.style.webkitUserSelect = 'none';

      try {
        window.getSelection?.()?.removeAllRanges?.();
      } catch {}

      this.sigBlockers = { prevent, finish, restore };
    },

    releaseSelection() {
      if (!this.sigBlockers) return;

      const { prevent, finish, restore } = this.sigBlockers;
      this.sigBlockers = null;

      document.removeEventListener('selectstart', prevent, true);
      document.removeEventListener('dragstart', prevent, true);
      window.removeEventListener('pointerup', finish, true);
      window.removeEventListener('pointercancel', finish, true);
      document.body.style.userSelect = restore.userSelect;
      document.body.style.webkitUserSelect = restore.webkitUserSelect;
    },

    startDraw(e) {
      // ctx 준비 전이나 전송 중에도 기본 동작(텍스트 선택)은 먼저 막는다.
      e.preventDefault();
      if (this.submitting) return;

      // 두 번째 손가락/손바닥 접촉으로 획이 초기화되지 않도록 주 포인터만 사용.
      if (e.isPrimary === false || this.sigPointerId !== null) return;

      if (!this.ctx) {
        this.setupCanvas(this.sigCanvas || e.currentTarget);
        if (!this.ctx) return;
      }

      this.sigPointerId = e.pointerId;
      this.drawing = true;
      this.blockSelection();

      try {
        this.sigCanvas.setPointerCapture?.(e.pointerId);
      } catch {}

      const { x, y } = this.pointerPos(e);
      this.ctx.beginPath();
      this.ctx.moveTo(x, y);
      // 점 하나만 찍어도 서명으로 인정되도록 시작점에 짧은 선을 그린다.
      this.ctx.lineTo(x + 0.1, y + 0.1);
      this.ctx.stroke();
      this.hasSignature = true;
    },

    moveDraw(e) {
      if (!this.drawing || !this.ctx) return;
      if (this.sigPointerId !== null && e.pointerId !== this.sigPointerId) {
        return;
      }
      e.preventDefault();

      // 빠르게 그을 때 중간 좌표가 누락돼 선이 각지는 것을 막는다.
      const points = e.getCoalescedEvents?.() || [e];
      for (const point of points) {
        const { x, y } = this.pointerPos(point);
        this.ctx.lineTo(x, y);
      }
      this.ctx.stroke();
    },

    endDraw(e) {
      if (e && this.sigPointerId !== null && e.pointerId !== this.sigPointerId) {
        return;
      }

      if (this.sigCanvas && this.sigPointerId !== null) {
        try {
          this.sigCanvas.releasePointerCapture?.(this.sigPointerId);
        } catch {}
      }

      this.sigPointerId = null;
      this.drawing = false;
      this.releaseSelection();
    },

    clearSignature() {
      if (!this.sigCanvas || !this.ctx) return;
      this.ctx.clearRect(0, 0, this.sigCanvas.width, this.sigCanvas.height);
      this.hasSignature = false;
    },
  };
}
